// ==================================================================================
// Digital Approval System (DAS) - Approvers Service
// ==================================================================================
// Service functions for approver lookup operations
// ==================================================================================

import { axiosClient } from '../../client/axiosClient';
import type { ApiResponse, PagedResult } from '../../types/common.types';
import type {
  ApproverDto,
  ApproverQueryParameters,
  ApproverType,
  RoleDto,
  DepartmentDto,
} from '../../types/das/approvers.types';
import { approverEndpoints } from '../../endpoints/das/approvers.endpoints';

/**
 * Approvers service
 */
export const approversService = {
  /**
   * Get all approvers (users and roles combined)
   */
  getAll: async (params?: ApproverQueryParameters): Promise<ApproverDto[]> => {
    const response = await axiosClient.get<ApiResponse<ApproverDto[]>>(
      approverEndpoints.getAll(params)
    );
    return response.data.data;
  },

  /**
   * Get approvers with pagination
   */
  getPaged: async (params: ApproverQueryParameters): Promise<PagedResult<ApproverDto>> => {
    const response = await axiosClient.get<ApiResponse<PagedResult<ApproverDto>>>(
      approverEndpoints.getPaged(params)
    );
    return response.data.data;
  },

  /**
   * Get user approvers only
   */
  getUsers: async (params?: ApproverQueryParameters): Promise<ApproverDto[]> => {
    const response = await axiosClient.get<ApiResponse<ApproverDto[]>>(
      approverEndpoints.getUsers(params)
    );
    return response.data.data;
  },

  /**
   * Get role approvers only
   */
  getRoles: async (params?: ApproverQueryParameters): Promise<ApproverDto[]> => {
    const response = await axiosClient.get<ApiResponse<ApproverDto[]>>(
      approverEndpoints.getRoles(params)
    );
    return response.data.data;
  },

  /**
   * Search approvers by name, email, or user code
   */
  search: async (
    query: string,
    type?: ApproverType,
    limit?: number
  ): Promise<ApproverDto[]> => {
    const response = await axiosClient.get<ApiResponse<ApproverDto[]>>(
      approverEndpoints.search(query, type, limit)
    );
    return response.data.data;
  },

  /**
   * Get user approver by ID
   */
  getUserById: async (id: string): Promise<ApproverDto> => {
    const response = await axiosClient.get<ApiResponse<ApproverDto>>(
      approverEndpoints.getUserById(id)
    );
    return response.data.data;
  },

  /**
   * Get role by ID
   */
  getRoleById: async (id: string): Promise<RoleDto> => {
    const response = await axiosClient.get<ApiResponse<RoleDto>>(
      approverEndpoints.getRoleById(id)
    );
    return response.data.data;
  },

  /**
   * Get users by role ID
   */
  getUsersByRole: async (roleId: string): Promise<ApproverDto[]> => {
    const response = await axiosClient.get<ApiResponse<ApproverDto[]>>(
      approverEndpoints.getUsersByRole(roleId)
    );
    return response.data.data;
  },

  /**
   * Get users by department ID
   */
  getUsersByDepartment: async (departmentId: string): Promise<ApproverDto[]> => {
    const response = await axiosClient.get<ApiResponse<ApproverDto[]>>(
      approverEndpoints.getUsersByDepartment(departmentId)
    );
    return response.data.data;
  },

  /**
   * Get all roles list
   */
  getAllRoles: async (activeOnly?: boolean): Promise<RoleDto[]> => {
    const response = await axiosClient.get<ApiResponse<RoleDto[]>>(
      approverEndpoints.getAllRoles(activeOnly)
    );
    return response.data.data;
  },

  /**
   * Get all departments list
   */
  getAllDepartments: async (activeOnly?: boolean): Promise<DepartmentDto[]> => {
    const response = await axiosClient.get<ApiResponse<DepartmentDto[]>>(
      approverEndpoints.getAllDepartments(activeOnly)
    );
    return response.data.data;
  },
};
